import {
  intersection as _intersection,
  isArray as _isArray,
  isSet as _isSet,
  keys as _keys,
  without as _without
} from 'lodash'
import {isSetsEqual, toArray} from './lang'

const _populateCustomDataFilterValueSet = (expressions, values) => {
  let filters = {}
  expressions = toArray(expressions)
  values = toArray(values)
  expressions.forEach((expression, index) => {
    if (filters[expression] === undefined) {
      filters[expression] = new Set()
    }
    filters[expression].add(values[index])
  })
  return filters
}

const _mergeCustomDataFilterValues = (filters1, filters2) => {
  // Return merged filter values of
  // - custom_data_expr, or
  // - custom_data_value
  //
  // For example,
  //
  // ['parties__contains'] + ['exposure'] --> ['parties__contains', 'exposure']
  const filters = _populateCustomDataFilterValueSet(
    toArray(filters1.custom_data_expr).concat(toArray(filters2.custom_data_expr)),
    toArray(filters1.custom_data_value).concat(toArray(filters2.custom_data_value))
  )

  let customDataFilters = {
    custom_data_expr: [],
    custom_data_value: []
  }
  Object.keys(filters).forEach(key => {
    filters[key].forEach(value => {
      customDataFilters.custom_data_expr.push(key)
      customDataFilters.custom_data_value.push(value)
    })
  })
  return customDataFilters
}

const _hasConflictedFilterValue = (value1, value2) => {
  if (_isArray(value1) && _isArray(value2)) {
    // Comparison should be made without considering item ordering.
    // Perform a set equality check.
    return !isSetsEqual(new Set(value1), new Set(value2))
  } else if (_isSet(value1) && _isSet(value2)) {
    // Perform a set equality check.
    return !isSetsEqual(value1, value2)
  } else {
    // Otherwise, perform a strict equality check.
    return value1 !== value2
  }
}

const _hasConflictedFilterValues = (filters1, filters2) => {
  const keys = _intersection(_keys(filters1), _keys(filters2))
  // return true if any key pair has no matching value
  return keys.some(key => _hasConflictedFilterValue(filters1[key], filters2[key]))
}

const _hasConflictedCustomDataFilterValues = (filters1, filters2) => {
  const customFilters1 = _populateCustomDataFilterValueSet(filters1.custom_data_expr, filters1.custom_data_value)
  const customFilters2 = _populateCustomDataFilterValueSet(filters2.custom_data_expr, filters2.custom_data_value)
  return _hasConflictedFilterValues(customFilters1, customFilters2)
}

const _hasCustomDataFilterKey = (keys) => {
  return keys.indexOf('custom_data_expr') > -1 || keys.indexOf('custom_data_value') > -1
}

export const hasConflictedFilterValues = (filters1, filters2) => {
  // Identify conflicted filter values
  let conflictedCustomData = false
  let conflictedData
  const keys = _intersection(_keys(filters1), _keys(filters2))

  if (_hasCustomDataFilterKey(keys)) {
    conflictedCustomData = _hasConflictedCustomDataFilterValues(filters1, filters2)
    conflictedData = _without(
      keys, 'custom_data_expr', 'custom_data_value'
    ).some(
      key => filters1[key] !== filters2[key]
    )
  } else {
    conflictedData = _hasConflictedFilterValues(filters1, filters2)
  }

  // Return true if there is one or more conflicted filter values between both filters
  return conflictedCustomData || conflictedData
}

export const populateCustomDataFilters = (filters1, filters2) => {
  const filterKeys1 = _keys(filters1)
  const filterKeys2 = _keys(filters2)
  if (_hasCustomDataFilterKey(filterKeys1) || _hasCustomDataFilterKey(filterKeys2)) {
    // Return custom data filters, for example,
    //
    // {
    //   custom_data_expr: ['parties__contains', 'exposure']
    //   custom_data_values: ['0d367d53-803b-4305-a484-864c086c0658', '49bd4f4a-d66b-4403-bbec-46f4edcd398a']
    // }
    return _mergeCustomDataFilterValues(filters1, filters2)
  }
  // Return empty object is no custom data filter found
  return {}
}
